import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  IconTrash as DeleteIcon,
  IconPencil as EditIcon,
  IconUser,
  IconMail
} from '@tabler/icons-react';
import { api } from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useUserSession } from '../contexts/UserSessionContext.jsx';
import { useCanEdit, useIsOwner } from '../components/RoleGate.jsx';
import { CenteredLoader } from '../components/LoadingIndicator';
import { useToast } from '../components/ToastNotification';
import { PageHero, SectionContainer, Row, Badge } from '../components/ds';

const ROLES = ['viewer', 'editor', 'owner'];

// Role -> Badge variant (DESIGN_SYSTEM.md section 8.3, v4 #1377: owner wears
// the bordered chip) and -> Row icon-box category, kept aligned so the icon
// tint echoes the badge color.
const roleBadgeVariant = (role) =>
  role === 'owner' ? 'owner' : role === 'editor' ? 'interactive' : 'neutral';
const roleCategory = (role) =>
  role === 'owner' ? 'warning' : role === 'editor' ? 'checking' : 'neutral';

const Members = ({ isAuthReady = false }) => {
  const { activeGroupId, activeGroup, activeGroupName, reload } = useGroup();
  const session = useUserSession();
  const { showError, showSuccess } = useToast();
  const canEdit = useCanEdit();
  const isOwner = useIsOwner();
  const callerUserId = session.user?.userId || null;

  const [members, setMembers] = useState(null);
  const [invites, setInvites] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  const openRename = () => {
    setRenameValue(activeGroup?.name || '');
    setRenameOpen(true);
  };

  const handleRenameSave = async () => {
    const trimmed = (renameValue || '').trim();
    if (!trimmed) {
      showError('Workspace name cannot be empty');
      return;
    }
    if (!activeGroup) {
      showError('Workspace not loaded');
      return;
    }
    setRenaming(true);
    try {
      await api.put(activeGroupId, '', { name: trimmed, version: activeGroup.version });
      showSuccess('Workspace renamed');
      setRenameOpen(false);
      await reload();
    } catch (err) {
      showError(err.body?.error || err.message || 'Rename failed');
    } finally {
      setRenaming(false);
    }
  };

  const load = useCallback(async () => {
    if (!activeGroupId) return;
    setLoading(true);
    setError(null);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        api.get(activeGroupId, '/members'),
        canEdit ? api.get(activeGroupId, '/invites').catch(() => ({ invites: [] })) : Promise.resolve({ invites: [] })
      ]);
      setMembers(membersRes?.members || []);
      setInvites(invitesRes?.invites || []);
    } catch (err) {
      setError(err.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [activeGroupId, canEdit]);

  useEffect(() => {
    if (isAuthReady && activeGroupId) load();
  }, [isAuthReady, activeGroupId, load]);

  // The send-result alert belongs to the workspace the invite was sent from;
  // a switch drops it so the new workspace's form starts clean (#1809).
  useEffect(() => {
    setLastInviteLink(null);
  }, [activeGroupId]);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
      showError('Enter a valid email');
      return;
    }
    setInviting(true);
    try {
      const res = await api.post(activeGroupId, '/invites', { email: inviteEmail.trim(), role: inviteRole });
      showSuccess('Invite created');
      const inviteId = res?.invite?.inviteId;
      if (inviteId) {
        setLastInviteLink(`${window.location.origin}/invitations/${encodeURIComponent(inviteId)}`);
      }
      setInviteEmail('');
      setInviteRole('viewer');
      await load();
    } catch (err) {
      showError(err.message || 'Invite failed');
    } finally {
      setInviting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!lastInviteLink) return;
    try {
      await navigator.clipboard.writeText(lastInviteLink);
      showSuccess('Link copied');
    } catch {
      showError('Copy failed: select and copy the link manually');
    }
  };

  const handleRoleChange = async (member, nextRole) => {
    if (member.role === nextRole) return;
    try {
      await api.put(activeGroupId, `/members/${encodeURIComponent(member.userId)}`, { role: nextRole });
      showSuccess('Role updated');
      await load();
      await reload();
    } catch (err) {
      showError(err.body?.error || err.message || 'Role change failed');
    }
  };

  const handleRemoveMember = async () => {
    const target = removeTarget;
    setRemoveTarget(null);
    try {
      await api.delete(activeGroupId, `/members/${encodeURIComponent(target.userId)}`);
      showSuccess('Member removed');
      await load();
    } catch (err) {
      showError(err.body?.error || err.message || 'Remove failed');
    }
  };

  const handleRevokeInvite = async (invite) => {
    try {
      await api.delete(activeGroupId, `/invites/${encodeURIComponent(invite.inviteId)}`);
      showSuccess('Invite revoked');
      await load();
    } catch (err) {
      showError(err.body?.error || err.message || 'Revoke failed');
    }
  };

  const handleResendInvite = async (invite) => {
    try {
      await api.post(activeGroupId, `/invites/${encodeURIComponent(invite.inviteId)}/resend`);
      showSuccess(`Resent to ${invite.email}`);
      await load();
    } catch (err) {
      showError(err.body?.error || err.message || 'Resend failed');
    }
  };

  if (!isAuthReady) return <CenteredLoader message="Initializing..." />;
  if (loading) return <CenteredLoader message="Loading members..." />;
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  const memberList = members || [];
  const inviteList = invites || [];

  return (
    <Box sx={{ pb: 8 }}>
      {/* v4 board (#1377): title carries the workspace name; the rename
          pencil sits inline with it. Member count lives in the section
          header count slot, so the title takes no bracket count the way
          Snapshots does (#1895). */}
      <PageHero
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            {activeGroupName ? `Members of Workspace: ${activeGroupName}` : 'Members'}
            {isOwner && (
              <IconButton size="small" aria-label="Rename workspace" onClick={openRename} sx={{ color: 'text.secondary' }}>
                <EditIcon size={16} />
              </IconButton>
            )}
          </Box>
        }
      />

      {/* v4 board layout: wide = members (1.2fr) beside the invite stack. */}
      <Box
        sx={{
          mt: { xs: '12px', md: '14px' },
          display: 'grid',
          gap: '16px',
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 1fr)' },
        }}
      >
        <SectionContainer title="Current members" count={memberList.length} category="interactive">
          {memberList.map((m, idx) => {
            const isSelf = m.userId === callerUserId;
            const primary = m.displayName || m.email || m.userId;
            const secondary = m.displayName && m.email ? m.email : null;
            const roleControl = (isOwner && !isSelf) ? (
              <Select
                size="small"
                value={m.role}
                onChange={(e) => handleRoleChange(m, e.target.value)}
                aria-label={`Role for ${primary}`}
              >
                {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            ) : (
              <Badge variant={roleBadgeVariant(m.role)}>{m.role}</Badge>
            );
            return (
              <Row
                key={m.userId}
                icon={<IconUser />}
                category={roleCategory(m.role)}
                name={`${primary}${isSelf ? ' (you)' : ''}`}
                meta={secondary}
                value={roleControl}
                actions={isOwner && !isSelf ? (
                  <IconButton
                    size="small"
                    aria-label={`Remove ${primary}`}
                    onClick={() => setRemoveTarget(m)}
                    sx={(theme) => ({
                      color: theme.palette.text.secondary,
                      padding: '2px',
                      '&:hover': { color: theme.palette.error.main },
                    })}
                  >
                    <DeleteIcon size={15} />
                  </IconButton>
                ) : undefined}
                last={idx === memberList.length - 1}
              />
            );
          })}
        </SectionContainer>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
        {canEdit && (
          <SectionContainer title="Send invite" category="budgets">
            <form onSubmit={handleSendInvite}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ pt: 1 }}>
                <TextField
                  label="Email"
                  type="email"
                  size="small"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  sx={{ flex: 1 }}
                />
                <Select
                  size="small"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  aria-label="Invite role"
                >
                  {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
                <Button type="submit" variant="contained" disabled={inviting}>
                  {inviting ? 'Sending...' : 'Send'}
                </Button>
              </Stack>
            </form>
            {lastInviteLink && (
              <Alert severity="info" sx={{ mt: 2 }} action={
                <Button color="inherit" size="small" onClick={handleCopyLink}>Copy</Button>
              }>
                <Typography variant="body2">Email sent. If they did not receive it, share this link directly:</Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{lastInviteLink}</Typography>
              </Alert>
            )}
          </SectionContainer>
        )}

        {canEdit && (
          <SectionContainer title="Pending invites" count={inviteList.length} category="warning">
            {inviteList.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>No pending invites.</Typography>
            ) : (
              inviteList.map((i, idx) => {
                const metaParts = [];
                if (i.createdAt) metaParts.push(`Sent ${new Date(i.createdAt).toLocaleDateString()}`);
                if (i.expiresAt) metaParts.push(`Expires ${new Date(i.expiresAt).toLocaleDateString()}`);
                return (
                  <Row
                    key={i.inviteId}
                    icon={<IconMail />}
                    category={roleCategory(i.role)}
                    name={i.email}
                    meta={metaParts.join(' · ') || null}
                    value={<Badge variant={roleBadgeVariant(i.role)}>{i.role}</Badge>}
                    actions={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" onClick={() => handleResendInvite(i)}>Resend</Button>
                        <Button size="small" color="error" onClick={() => handleRevokeInvite(i)}>Revoke</Button>
                      </Box>
                    }
                    last={idx === inviteList.length - 1}
                  />
                );
              })
            )}
          </SectionContainer>
        )}
        </Box>
      </Box>

      <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Remove member?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove {removeTarget?.displayName || removeTarget?.email || removeTarget?.userId} from the group?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemoveMember}>Remove</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameOpen} onClose={() => !renaming && setRenameOpen(false)}>
        <DialogTitle>Rename workspace</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workspace name"
            fullWidth
            inputProps={{ maxLength: 100 }}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            disabled={renaming}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={renaming}>Cancel</Button>
          <Button variant="contained" onClick={handleRenameSave} disabled={renaming}>
            {renaming ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Members;
